// Real-time chat client (SignalR) for the LegalLawyersonam platform.
//
// This is a thin, resilient singleton wrapper around @microsoft/signalr:
//   - one shared connection for the whole tab
//   - auto-reconnect, and auto re-join of any rooms after a reconnect
//   - every call is wrapped so that if the socket cannot connect, callers
//     simply keep working through the REST + polling fallback (no crashes)
//
// Persistence is always handled by the REST API (SQL Server). This layer only
// delivers the already-saved message instantly to the other side.
import * as signalR from "@microsoft/signalr";
import { API_BASE_URL } from "./apiConfig";

// API_BASE_URL looks like "http://localhost:5198/api" -> hub is at "/hubs/chat"
const HUB_URL = `${API_BASE_URL.replace(/\/api\/?$/, "")}/hubs/chat`;

let connection = null;
let startPromise = null;
const joinedSessions = new Map(); // sessionId -> userId
const joinedUsers = new Set();

function getToken() {
  return sessionStorage.getItem("token") || localStorage.getItem("token") || "";
}

function buildConnection() {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, { accessTokenFactory: getToken })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  // After an automatic reconnect, SignalR drops group membership — re-join with preserved userId.
  conn.onreconnected(() => {
    joinedSessions.forEach((uId, sId) => {
      conn.invoke("JoinSession", sId, uId).catch(() => {});
    });
    joinedUsers.forEach((u) => conn.invoke("JoinUser", u).catch(() => {}));
  });

  return conn;
}

export function getConnection() {
  if (!connection) connection = buildConnection();
  return connection;
}

// Ensure the shared connection is started exactly once.
export async function ensureConnected() {
  const conn = getConnection();
  if (conn.state === signalR.HubConnectionState.Connected) return conn;
  if (conn.state === signalR.HubConnectionState.Connecting && startPromise) {
    try {
      await startPromise;
      return conn;
    } catch {
      // ignore
    }
  }
  if (!startPromise) {
    startPromise = conn
      .start()
      .then(() => {
        startPromise = null;
        return conn;
      })
      .catch((err) => {
        startPromise = null;
        throw err;
      });
  }
  try {
    await startPromise;
  } catch (e) {
    // will fallback to polling
  }
  return conn;
}

// Subscribe to a hub event. Returns an unsubscribe function.
export function on(event, handler) {
  const conn = getConnection();
  conn.on(event, handler);
  return () => {
    try {
      conn.off(event, handler);
    } catch {
      /* no-op */
    }
  };
}

export async function joinSession(sessionId, userId = null) {
  if (!sessionId && sessionId !== 0) return false;
  const key = String(sessionId);
  const parsedUserId = userId ? Number(userId) : null;
  joinedSessions.set(key, parsedUserId);
  try {
    const conn = await ensureConnected();
    await conn.invoke("JoinSession", key, parsedUserId);
    return true;
  } catch (e) {
    console.warn(
      "[chatHub] joinSession failed — falling back to polling:",
      e?.message || e,
    );
    return false;
  }
}

export async function leaveSession(sessionId, userId = null) {
  if (!sessionId && sessionId !== 0) return;
  const key = String(sessionId);
  joinedSessions.delete(key);
  try {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Connected
    ) {
      await connection.invoke("LeaveSession", key, userId ? Number(userId) : null);
    }
  } catch {
    /* no-op */
  }
}

// Send message directly over SignalR ChatHub
export async function sendHubMessage(sessionId, messageText, senderId = null, receiverId = null, attachmentUrl = null) {
  const conn = await ensureConnected();
  return await conn.invoke(
    "SendMessage",
    String(sessionId),
    messageText,
    senderId ? Number(senderId) : null,
    receiverId ? Number(receiverId) : null,
    attachmentUrl || null,
  );
}

// Mark messages read directly over SignalR ChatHub
export async function markHubSessionRead(sessionId, userId = null) {
  try {
    const conn = await ensureConnected();
    if (conn && conn.state === signalR.HubConnectionState.Connected) {
      await conn.invoke("MarkSessionRead", String(sessionId), userId ? Number(userId) : null);
    }
  } catch {
    /* no-op */
  }
}

export async function checkSessionPresence(sessionId) {
  if (!sessionId) return { bothOnline: false };
  try {
    const conn = await ensureConnected();
    return await conn.invoke("CheckSessionPresence", String(sessionId));
  } catch {
    return { bothOnline: false };
  }
}

export async function joinUser(userId) {
  if (userId === null || userId === undefined) return;
  const key = String(userId);
  joinedUsers.add(key);
  try {
    const conn = await ensureConnected();
    if (conn) {
      await conn.invoke("JoinUser", key);
    }
  } catch (e) {
    console.warn(
      "[chatHub] joinUser failed - falling back to polling:",
      e?.message || e,
    );
  }
}

export async function joinAdmin() {
  try {
    const conn = await ensureConnected();
    if (conn) {
      await conn.invoke("JoinAdmin");
    }
  } catch (e) {
    console.warn("[chatHub] joinAdmin failed:", e?.message || e);
  }
}

export async function leaveUser(userId) {
  if (!userId && userId !== 0) return;
  const key = String(userId);
  joinedUsers.delete(key);
  try {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Connected
    ) {
      await connection.invoke("LeaveUser", key);
    }
  } catch {
    /* no-op */
  }
}

export async function sendTyping(sessionId, senderId, isTyping) {
  try {
    const conn = await ensureConnected();
    if (conn && conn.state === signalR.HubConnectionState.Connected) {
      await conn.invoke(
        "Typing",
        String(sessionId),
        String(senderId),
        !!isTyping,
      );
    }
  } catch {
    /* no-op */
  }
}

// ---- Call signaling (ring/accept/decline) — the actual audio/video streams
// travel peer-to-peer over WebRTC once both sides accept; see sendWebRtc* below.
// The hub resolves the callee (and, for respond/end, the other participant)
// from the ChatSession row itself — the client only needs to say who's
// calling, not who should receive it.
export async function requestCall(sessionId, callerId, callerName, callType) {
  const conn = await ensureConnected();
  await conn.invoke(
    "RequestCall",
    String(sessionId),
    String(callerId),
    callerName || "",
    callType || "voice",
  );
}

export async function respondToCall(sessionId, accepted, responderName) {
  const conn = await ensureConnected();
  await conn.invoke(
    "RespondToCall",
    String(sessionId),
    !!accepted,
    responderName || "",
  );
}

export async function endCall(sessionId) {
  try {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Connected
    ) {
      await connection.invoke("EndCall", String(sessionId));
    }
  } catch {
    /* no-op */
  }
}

// ---- WebRTC signaling relay — carries only offer/answer/ICE JSON, never media.
export async function sendWebRtcOffer(sessionId, sdp) {
  const conn = await ensureConnected();
  await conn.invoke("WebRtcOffer", String(sessionId), JSON.stringify(sdp));
}

export async function sendWebRtcAnswer(sessionId, sdp) {
  const conn = await ensureConnected();
  await conn.invoke("WebRtcAnswer", String(sessionId), JSON.stringify(sdp));
}

export async function sendWebRtcIceCandidate(sessionId, candidate) {
  try {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Connected
    ) {
      await connection.invoke(
        "WebRtcIceCandidate",
        String(sessionId),
        JSON.stringify(candidate),
      );
    }
  } catch {
    /* no-op */
  }
}

export { signalR };
